import React, { useRef, useEffect, memo } from 'react';
import { FacetItem, FacetKeyValuePair } from '../../../types/search';
import { getStatusIconModifier } from '../../../constants/jobStatuses';
import { Button } from 'react-aria-components';

type StatusFilterButtonProps = Readonly<{
  facetKey: string;
  item: FacetItem & { displayTotal?: number };
  onApply: (props: FacetKeyValuePair) => void;
  onRemove: (props: FacetKeyValuePair) => void;
}>;

function StatusFilterButton({ facetKey, item, onApply, onRemove }: StatusFilterButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (buttonRef.current) {
      if (item.isSelected) {
        buttonRef.current.setAttribute('aria-selected', 'true');
      } else {
        buttonRef.current.removeAttribute('aria-selected');
      }
    }
  }, [item.isSelected]);

  return (
    <Button
      ref={buttonRef}
      className="indicator-btn"
      aria-label={`${item.isSelected ? 'Remove' : 'Apply'} ${item.label || item.value} filter`}
      aria-pressed={item.isSelected}
      onClick={() =>
        item.isSelected ? onRemove({ facetKey, itemValue: item.value }) : onApply({ facetKey, itemValue: item.value })
      }
      type="button"
      data-selected={item.isSelected}
    >
      <svg
        className={`icon indicator-btn__icon indicator-btn__icon--${getStatusIconModifier(item?.label)}`}
        aria-hidden="true"
      >
        <use href="/dist/assets/icons/sprite.svg#circle-with-border"></use>
      </svg>
      <span className="indicator-btn__label">{item.label || item.value}</span>
      {item.displayTotal !== undefined && (
        <span className="indicator-btn__count" aria-label={`${item.displayTotal} items`}>
          {item.displayTotal}
        </span>
      )}
      {item.isSelected && (
        <svg className="icon indicator-btn__remove-icon" aria-hidden="true">
          <use href="/dist/assets/icons/sprite.svg#x-mark-medium"></use>
        </svg>
      )}
    </Button>
  );
}

export default memo(StatusFilterButton);
